import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Send, Sparkles, ThumbsUp, ThumbsDown } from 'lucide-react';
import { trackCTAClick } from '../utils/analytics';
import { useWebsiteChat, SUGGESTED_PROMPTS, ChatMessage } from '../hooks/useWebsiteChat';

interface HeroWithChatProps {
  onBookDemo: () => void;
}

const HeroWithChat: React.FC<HeroWithChatProps> = ({ onBookDemo }) => {
  const [inputValue, setInputValue] = useState('');
  const [isStartingUp, setIsStartingUp] = useState(true);
  const [visiblePromptCount, setVisiblePromptCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    messages,
    isLoading,
    error,
    sendMessage,
    sendPrompt,
    submitFeedback,
    showEngagementPrompts,
    currentFollowUpQuestions,
    handleEngagementPromptClick,
    onUserInputChange,
  } = useWebsiteChat();

  // AI startup animation sequence
  useEffect(() => {
    // Initial startup pulse duration
    const startupTimer = setTimeout(() => {
      setIsStartingUp(false);
      // Start revealing prompts one by one
      let count = 0;
      const promptInterval = setInterval(() => {
        count++;
        setVisiblePromptCount(count);
        if (count >= SUGGESTED_PROMPTS.length) {
          clearInterval(promptInterval);
        }
      }, 150); // 150ms between each prompt
    }, 1200); // 1.2s startup animation

    return () => clearTimeout(startupTimer);
  }, []);

  const handleBookDemoClick = () => {
    trackCTAClick('book_demo', 'hero_simplified');
    onBookDemo();
  };

  // Scroll to bottom of chat when messages change or engagement prompts appear
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages, showEngagementPrompts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue;
    setInputValue('');
    await sendMessage(message);
  };

  const handlePromptClick = async (prompt: string) => {
    if (isLoading) return;
    await sendPrompt(prompt);
  };

  return (
    <section className="pt-24 pb-20 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Headline and CTA */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              <span className="whitespace-nowrap">The Human Guide to</span>
              <span className="text-teal-400 block mt-2">AI Transformation</span>
            </h1>

            <p className="text-xl sm:text-2xl text-slate-300 mb-10 max-w-xl leading-relaxed">
              Understand your team. Build their skills. Navigate change together.
            </p>

            <button
              onClick={handleBookDemoClick}
              className="inline-flex items-center bg-teal-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-400 transition-all transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-teal-500/25"
            >
              Book a Demo
              <ArrowRight className="ml-2" size={20} />
            </button>
          </div>

          {/* Right Column - Integrated Chat */}
          <div className="bg-slate-800/50 rounded-2xl border-2 border-teal-500/50 overflow-hidden animate-border-pulse">
            {/* Chat Header */}
            <div className="flex items-center gap-2 p-4 border-b border-slate-700">
              <Sparkles className="text-teal-400" size={20} />
              <span className="text-slate-300">Welcome! What can I tell you about NeuPath?</span>
            </div>

            {/* Chat Content Area */}
            <div className="h-80 overflow-y-auto p-4 pb-24">
              {/* Show startup animation then suggested prompts if no messages */}
              {messages.length === 0 && (
                <div className="space-y-3">
                  {/* AI Starting Up indicator */}
                  {isStartingUp && (
                    <div className="flex flex-col items-center justify-center py-8">
                      <div className="relative">
                        <Sparkles
                          size={32}
                          className="text-teal-400 animate-pulse-glow"
                        />
                        <div className="absolute inset-0 bg-teal-400/20 rounded-full blur-xl animate-pulse-glow" />
                      </div>
                      <p className="text-slate-400 text-sm mt-4 animate-pulse">Initializing AI assistant...</p>
                    </div>
                  )}

                  {/* Suggested prompts with staggered reveal */}
                  {!isStartingUp && SUGGESTED_PROMPTS.map((prompt, index) => (
                    <button
                      key={prompt}
                      onClick={() => handlePromptClick(prompt)}
                      disabled={isLoading || index >= visiblePromptCount}
                      className={`w-full text-left flex items-center gap-3 p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors disabled:opacity-50 group ${
                        index < visiblePromptCount ? 'animate-slide-up' : 'opacity-0'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <span className="text-teal-400 group-hover:text-teal-300">
                        <Sparkles size={16} />
                      </span>
                      <span>{prompt}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Message list */}
              {messages.length > 0 && (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div key={index}>
                      <MessageBubble message={message} />
                      {/* Feedback icons for assistant messages */}
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mt-2 ml-1">
                          <button
                            onClick={() => submitFeedback(index, 'up')}
                            disabled={message.feedback != null}
                            className={`p-1.5 rounded transition-colors ${
                              message.feedback === 'up'
                                ? 'text-teal-400'
                                : message.feedback != null
                                ? 'text-slate-600 cursor-not-allowed'
                                : 'text-slate-400 hover:text-teal-400'
                            }`}
                            aria-label="Thumbs up"
                          >
                            <ThumbsUp size={16} />
                          </button>
                          <button
                            onClick={() => submitFeedback(index, 'down')}
                            disabled={message.feedback != null}
                            className={`p-1.5 rounded transition-colors ${
                              message.feedback === 'down'
                                ? 'text-orange-400'
                                : message.feedback != null
                                ? 'text-slate-600 cursor-not-allowed'
                                : 'text-slate-400 hover:text-orange-400'
                            }`}
                            aria-label="Thumbs down"
                          >
                            <ThumbsDown size={16} />
                          </button>
                        </div>
                      )}
                      {/* Follow-up prompts after the latest assistant message */}
                      {showEngagementPrompts && index === messages.length - 1 && message.role === 'assistant' && currentFollowUpQuestions.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3 animate-fade-in">
                          {currentFollowUpQuestions.map((prompt, i) => (
                            <button
                              key={i}
                              onClick={() => handleEngagementPromptClick(prompt)}
                              className="text-xs bg-slate-700/50 text-teal-400 px-3 py-1.5 rounded-full hover:bg-slate-600 transition-colors border border-slate-600"
                            >
                              {prompt}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Loading indicator */}
                  {isLoading && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-sm">Thinking...</span>
                    </div>
                  )}

                  {/* Error message */}
                  {error && (
                    <div className="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Chat Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-slate-700">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    onUserInputChange();
                  }}
                  placeholder="Ask your questions about NeuPath here"
                  disabled={isLoading}
                  className="flex-1 bg-slate-700 text-white placeholder-slate-400 px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="bg-teal-500 text-white p-3 rounded-full hover:bg-teal-400 transition-colors disabled:opacity-50 disabled:hover:bg-teal-500"
                  aria-label="Send message"
                >
                  <Send size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Gradient fade to white */}
      <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-b from-transparent to-gray-50 pointer-events-none" />
    </section>
  );
};

// Helper to render text with clickable links
const renderTextWithLinks = (text: string, isUser: boolean) => {
  // First, convert markdown links [text](url) to just the URL
  const textWithoutMarkdownLinks = text.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '$2'
  );

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = textWithoutMarkdownLinks.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      // Clean trailing punctuation from URLs
      const cleanUrl = part.replace(/[.,;:!?)]+$/, '');
      const trailingPunctuation = part.slice(cleanUrl.length);
      return (
        <React.Fragment key={index}>
          <a
            href={cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline hover:opacity-80 ${
              isUser ? 'text-white' : 'text-teal-400'
            }`}
          >
            {cleanUrl}
          </a>
          {trailingPunctuation}
        </React.Fragment>
      );
    }
    return part;
  });
};

// Message bubble component
const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] px-4 py-2 rounded-2xl ${
          isUser
            ? 'bg-teal-500 text-white rounded-br-md'
            : 'bg-slate-700 text-slate-200 rounded-bl-md'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap">{renderTextWithLinks(message.content, isUser)}</p>
      </div>
    </div>
  );
};

export default HeroWithChat;
